import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bird, BookOpen, Building2, Coffee, Compass, Heart, Landmark, Leaf, MapPin, Mountain, Sailboat, Sparkles, Star, Trees, Users, UtensilsCrossed, Waves } from 'lucide-react';
import { useLang } from '../i18n';
import { useCatalog } from '../data/catalog';

const INSPIRATION_ICONS = { Trees, Landmark, Waves, Coffee, UtensilsCrossed, Mountain, Bird, Users, Heart, MapPin, Sailboat, Sparkles, Star, Compass };
import { Kicker, Reveal } from './Reveal';
import { PartnerCard } from './PartnerCard';
import { AccommodationCard } from './AccommodationCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** 05 — Places to Stay: real stays only (dedicated accommodations first, then verified local network) */
export const FeaturedStays = () => {
  const { t } = useLang();
  const [accs, setAccs] = useState([]);
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    fetch(`${API}/accommodations`).then((r) => (r.ok ? r.json() : [])).then((d) => setAccs((Array.isArray(d) ? d : []).slice(0, 3))).catch(() => {});
    fetch(`${API}/partners?type=accommodation`).then((r) => (r.ok ? r.json() : [])).then((d) => setPartners((Array.isArray(d) ? d : []).slice(0, 3))).catch(() => {});
  }, []);
  if (accs.length === 0 && partners.length === 0) return null;
  return (
    <section data-testid="featured-stays-section" className="bg-eco-cream py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Kicker>{t('stays.home.k')}</Kicker>
            <h2 className="eco-section-title mt-4 text-eco-ink">{t('stays.home.t')}</h2>
            <p className="mt-2 text-sm text-eco-ink/55">{t('stays.home.s')}</p>
          </div>
          <Link to="/accommodations" data-testid="stays-view-all" className="eco-btn-text group">{t('stays.home.cta')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" /></Link>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {accs.map((a, i) => <Reveal key={a.id} delay={i * 0.08}><AccommodationCard acc={a} /></Reveal>)}
          {accs.length === 0 && partners.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <PartnerCard partner={p} typeLabel={t('type.accommodation')} testid={`home-stay-${p.id}`} cta={t('cta.checkstay')} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/** 10 — Travel Guides band (real published guides only) */
export const GuidesBand = () => {
  const { t } = useLang();
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    fetch(`${API}/guides`).then((r) => (r.ok ? r.json() : [])).then((d) => setGuides((Array.isArray(d) ? d : []).slice(0, 3))).catch(() => {});
  }, []);
  if (guides.length === 0) return null;
  return (
    <section data-testid="home-guides-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Kicker>{t('nav.guides')}</Kicker>
            <h2 className="eco-section-title mt-4 text-eco-ink">{t('guides.home.t')}</h2>
          </div>
          <Link to="/travel-guides" data-testid="guides-view-all" className="eco-btn-text group">{t('guides.home.cta')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" /></Link>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {guides.map((g, i) => (
            <Reveal key={g.slug} delay={i * 0.08}>
              <Link to={`/travel-guides/${g.slug}`} data-testid={`home-guide-${g.slug}`} className="group eco-card block !p-0 overflow-hidden">
                {g.img && <img src={g.img} alt="" loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]" />}
                <div className="p-5">
                  <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-eco-gold"><BookOpen size={12} /> {g.destination}</p>
                  <h3 className="eco-card-title mt-1.5">{g.title}</h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/** 11 — For Travel Partners (B2B) */
export const TravelPartnersBand = () => {
  const { t } = useLang();
  return (
    <section data-testid="home-b2b-section" className="bg-eco-deep py-24 md:py-28">
      <div className="mx-auto max-w-5xl px-5 text-center md:px-8">
        <Reveal>
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-eco-gold/15 text-eco-gold"><Building2 size={20} /></span>
          <h2 className="mt-6 font-serif text-3xl font-semibold text-white md:text-4xl">{t('b2b.home.t')}</h2>
          <p className="eco-body mx-auto mt-4 max-w-2xl text-white/65">{t('b2b.home.s')}</p>
          <Link to="/contact" data-testid="b2b-home-cta" className="mt-8 inline-flex min-h-12 items-center gap-2 rounded-[10px] bg-eco-gold px-7 text-[15px] font-semibold text-eco-deep transition-colors hover:bg-[#b8944f]">
            {t('b2b.home.cta')} <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

/** 12 — Our Story teaser (real narrative + authentic Cantinho Barreto photo provided by the owner) */
export const OurStoryBand = () => {
  const { t } = useLang();
  return (
    <section data-testid="home-story-section" className="bg-eco-cream py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl shadow-xl">
              <img
                src={`${API}/files/eco-collection/uploads/7013f2b9-78fd-438a-b6ad-b18239705828.webp`}
                alt={t('story.about.c1')}
                loading="lazy"
                data-testid="home-story-image"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <p className="mt-3 text-center text-[11px] text-eco-ink/65">{t('story.about.c1')} · Cantinho Barreto, Príncipe</p>
          </Reveal>
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-eco-green text-eco-gold"><Leaf size={20} /></span>
              <Kicker>{t('story.home.k')}</Kicker>
            </div>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-eco-ink md:text-4xl">{t('story.home.t')}</h2>
            <p className="eco-body mt-5 text-eco-ink/65">{t('story.home.s')}</p>
            <Link to="/about#our-story" data-testid="story-home-cta" className="eco-btn-text mt-6 inline-flex">{t('story.home.cta')} <ArrowRight size={15} /></Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/** 13 — Final CTA */
export const FinalCta = () => {
  const { t } = useLang();
  return (
    <section data-testid="home-final-cta" className="bg-eco-green py-20 md:py-28">
      <div className="mx-auto max-w-4xl px-5 text-center md:px-8">
        <Reveal>
          <h2 className="font-serif text-3xl font-semibold leading-tight text-white md:text-5xl">{t('final.t')}</h2>
          <p className="eco-body mx-auto mt-4 max-w-xl text-white/70">{t('final.s')}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/plan-my-trip" data-testid="final-plan-cta" className="inline-flex min-h-12 items-center gap-2 rounded-[10px] bg-eco-gold px-7 text-[15px] font-semibold text-eco-deep transition-colors hover:bg-[#b8944f]">{t('nav.plan')} <ArrowRight size={16} /></Link>
            <Link to="/destinations" data-testid="final-explore-cta" className="inline-flex min-h-12 items-center gap-2 rounded-[10px] border border-white/25 px-7 text-[15px] font-semibold text-white transition-colors hover:border-eco-gold hover:text-eco-gold">{t('nav.explore')}</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/** 03 — Find Your Inspiration: admin-managed taxonomy, only categories with real products */
export const InspirationSection = () => {
  const { t, lang } = useLang();
  const { experiences } = useCatalog();
  const [cats, setCats] = useState([]);
  useEffect(() => {
    fetch(`${API}/inspiration-categories`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setCats((Array.isArray(d) ? d : []).filter((c) => c.experience_slugs.some((s) => experiences.some((e) => e.slug === s)))))
      .catch(() => {});
  }, [experiences]);
  if (cats.length === 0) return null;
  return (
    <section id="inspiracao" data-testid="inspiration-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal className="text-center">
          <Kicker>{t('insp.k')}</Kicker>
          <h2 className="eco-section-title mx-auto mt-4 max-w-2xl text-eco-ink">{t('insp.t')}</h2>
          <p className="mt-3 text-sm text-eco-ink/55">{t('insp.s')}</p>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {cats.map((c, i) => {
            const count = c.experience_slugs.filter((s) => experiences.some((e) => e.slug === s)).length;
            return (
              <Reveal key={c.slug} delay={(i % 4) * 0.06}>
                <Link
                  to={`/experiences?inspiration=${c.slug}`}
                  data-testid={`inspiration-card-${c.slug}`}
                  className="group flex h-full flex-col items-center rounded-2xl border border-eco-ink/10 bg-eco-cream p-6 text-center transition-all hover:border-eco-gold/50 hover:shadow-md"
                >
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-eco-green text-eco-gold transition-transform duration-300 group-hover:scale-110">
                    {(() => { const Icon = INSPIRATION_ICONS[c.icon] || Compass; return <Icon size={18} />; })()}
                  </span>
                  <span className="mt-3 text-sm font-semibold text-eco-ink">{c.name[lang] || c.name.pt}</span>
                  {(c.description?.[lang] || c.description?.pt) && <span className="mt-1 text-xs leading-relaxed text-eco-ink/55">{c.description[lang] || c.description.pt}</span>}
                  <span className="mt-2 text-xs font-bold text-eco-gold"><span className="eco-number">{count}</span> {t(count === 1 ? 'insp.count.one' : 'insp.count.other')}</span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
